/**
 * useConvex — small reactive wrapper around the HTTP `runConvexQuery` for OS v2.
 *
 * The legacy OS pages do this manually with `useState + useEffect + getToken`.
 * This hook collapses the boilerplate so v2 callsites stay readable.
 *
 * Why HTTP and not the React provider?
 *   The legacy app already uses `runConvexQuery` (HTTP) for fetches; this is the
 *   established pattern and avoids forcing a Provider rewire. We can swap to
 *   `convex/react` reactive hooks later without touching call sites if we keep
 *   the signature `{ data, loading, error, refresh }`.
 *
 * Falls back to `null` / `undefined` data when:
 *   - Convex isn't configured (`hasConvexClient` is false)
 *   - The user isn't signed in (no token)
 *   - The query throws (logged to console, surfaced as `error`)
 *
 * Callers in OS v2 are expected to render with mock content when `data == null`,
 * so the surface stays demo-able for cold preview traffic and Computer Use QA.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { requireConvexAuthToken, useAppAuth } from '@/lib/appAuth';
import { runConvexQuery, runConvexMutation, hasConvexClient } from '@/lib/convex';

export interface UseConvexQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  /** Re-fetch on demand (e.g. after a mutation). */
  refresh: () => void;
}

/**
 * Run a Convex query keyed on its name + serialised args.
 *
 * The args object is shallow-stringified for the dep-key. Pass simple
 * primitive bags. If you need a stable identity for nested objects, hoist them
 * to a `useMemo` upstream.
 */
export function useConvexQuery<T>(
  name: string,
  args: Record<string, unknown> = {},
  options: { skip?: boolean } = {},
): UseConvexQueryResult<T> {
  const { getToken, isSignedIn, isLoaded } = useAppAuth();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(hasConvexClient && !options.skip);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  // Cancel-on-unmount + cancel-on-re-fetch flag.
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const argsKey = JSON.stringify(args);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (options.skip) {
      setLoading(false);
      return;
    }
    if (!hasConvexClient) {
      setLoading(false);
      return;
    }
    if (!isLoaded) {
      // Wait for Clerk to settle before deciding signed-in vs out.
      return;
    }
    if (!isSignedIn) {
      setLoading(false);
      setData(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = await requireConvexAuthToken(getToken);
        const result = await runConvexQuery<T>(name, JSON.parse(argsKey), token);
        if (cancelled) return;
        setData((result as T | null) ?? null);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        // Don't spam the console on auth-not-yet-ready failures; let the caller decide.
        console.warn(`[useConvexQuery] ${name} failed:`, err);
        setError(err as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // We deliberately depend on the *string* of args, not the object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, argsKey, isSignedIn, isLoaded, options.skip, tick]);

  return { data, loading, error, refresh };
}

// ── Mutations ──────────────────────────────────────────────────────────

export interface UseConvexMutationResult<TArgs, TResult> {
  mutate: (args: TArgs) => Promise<TResult | null>;
  pending: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * Run a Convex mutation by name. Caller invokes `mutate(args)`; the hook
 * handles Clerk token + pending + error state. Returns `null` if Convex isn't
 * configured or the user isn't signed in (so callers can fall back to local
 * UI state).
 */
export function useConvexMutation<TArgs extends Record<string, unknown>, TResult>(
  name: string,
): UseConvexMutationResult<TArgs, TResult> {
  const { getToken, isSignedIn } = useAppAuth();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (args: TArgs): Promise<TResult | null> => {
      if (!hasConvexClient || !isSignedIn) {
        return null;
      }
      setPending(true);
      setError(null);
      try {
        const token = await requireConvexAuthToken(getToken);
        const result = await runConvexMutation<TResult>(name, args, token);
        return (result as TResult | null) ?? null;
      } catch (err) {
        console.warn(`[useConvexMutation] ${name} failed:`, err);
        setError(err as Error);
        throw err;
      } finally {
        setPending(false);
      }
    },
    [getToken, isSignedIn, name],
  );

  const reset = useCallback(() => setError(null), []);

  return { mutate, pending, error, reset };
}
